/**
 * Progress insights: flatten reportExercise docs to data points and derive metrics.
 * Used for 3-month client dashboard (workouts completed, volume, PRs, most improved).
 */

import { startOfWeek, subWeeks } from "date-fns";
import { normalizeFirestoreDate } from "../../../lib/firestoreDate";

/**
 * Date for charts / volume: **completedOn** when the workout finished; else **createdOn** (edge cases).
 * Does not use `dateIndex` (mobile ties it to session start, not completion).
 * @param {object} workoutDoc - reportExercise document
 * @returns {Date}
 */
function getWorkoutDate(workoutDoc) {
  const completed = normalizeFirestoreDate(workoutDoc.completedOn);
  if (completed) return completed;
  const created = normalizeFirestoreDate(workoutDoc.createdOn);
  if (created) return created;
  return new Date(0);
}

/**
 * Get reps for a set (completedReps or reps).
 * @param {object} setOrNested
 * @returns {number}
 */
function getReps(setOrNested) {
  const r = setOrNested.completedReps ?? setOrNested.reps;
  const n = Number(r);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Get weight for a set.
 * @param {object} setOrNested
 * @returns {number}
 */
function getWeight(setOrNested) {
  const n = Number(setOrNested.weight);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Flatten a single workout doc to data points: one per set (or per nested exercise in super_sets).
 * Skips skipped exercises/sets.
 * @param {object} workoutDoc - reportExercise document with reportData.exercises
 * @returns {{ date: Date, exerciseId: string, exerciseName: string, weight: number, reps: number, volume: number }[]}
 */
export function flattenWorkoutToDataPoints(workoutDoc) {
  const points = [];
  const date = getWorkoutDate(workoutDoc);
  const exercises = workoutDoc.reportData?.exercises ?? [];
  for (const ex of exercises) {
    if (ex.skipped === true) continue;
    if (!ex.sets || !Array.isArray(ex.sets)) continue;
    if (ex.type === "super_set") {
      for (const set of ex.sets) {
        if (set.skipped === true) continue;
        const nested = set.exercises ?? [];
        for (const nestedEx of nested) {
          if (nestedEx.skipped === true) continue;
          const weight = getWeight(nestedEx);
          const reps = getReps(nestedEx);
          const volume = weight * reps;
          points.push({
            date,
            exerciseId: nestedEx.exercise ?? nestedEx.id ?? "",
            exerciseName: nestedEx.exerciseName ?? "Unknown",
            weight,
            reps,
            volume,
          });
        }
      }
    } else {
      for (const set of ex.sets) {
        if (set.skipped === true) continue;
        const weight = getWeight(set);
        const reps = getReps(set);
        const volume = weight * reps;
        points.push({
          date,
          exerciseId: ex.exercise ?? ex.id ?? "",
          exerciseName: ex.exerciseName ?? "Unknown",
          weight,
          reps,
          volume,
        });
      }
    }
  }
  return points;
}

/**
 * Aggregate data points to one row per (exerciseId, date): top-set weight, reps at that load, total volume.
 * Reps are averaged across sets performed at the heaviest load that day (not summed across sets).
 * Display name is taken from the latest occurrence (by date) for that exerciseId.
 * @param {object[]} workouts - list of reportExercise docs
 * @returns {{ exerciseId: string, exerciseName: string, date: Date, maxWeight: number, maxReps: number, totalVolume: number }[]}
 */
export function aggregateByExerciseAndDate(workouts) {
  const byKey = new Map();
  for (const doc of workouts) {
    const points = flattenWorkoutToDataPoints(doc);
    const date = getWorkoutDate(doc);
    for (const p of points) {
      const key = `${p.exerciseId}|${date.getTime()}`;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, {
          exerciseId: p.exerciseId,
          exerciseName: p.exerciseName,
          date,
          sets: [{ weight: p.weight, reps: p.reps }],
          totalVolume: p.volume ?? 0,
        });
      } else {
        existing.sets.push({ weight: p.weight, reps: p.reps });
        existing.totalVolume += p.volume ?? 0;
        if (p.date > existing.date) existing.exerciseName = p.exerciseName;
      }
    }
  }

  return Array.from(byKey.values()).map((entry) => {
    const sets = entry.sets ?? [];
    const maxWeight = sets.reduce((m, s) => Math.max(m, s.weight ?? 0), 0);

    let maxReps = 0;
    if (maxWeight > 0) {
      const atTop = sets.filter(
        (s) => (s.weight ?? 0) === maxWeight && (s.reps ?? 0) > 0,
      );
      maxReps = atTop.length
        ? Math.round(atTop.reduce((sum, s) => sum + (s.reps ?? 0), 0) / atTop.length)
        : 0;
    } else {
      const repsPositive = sets.map((s) => s.reps ?? 0).filter((r) => r > 0);
      maxReps = repsPositive.length
        ? Math.round(repsPositive.reduce((a, b) => a + b, 0) / repsPositive.length)
        : 0;
    }

    return {
      exerciseId: entry.exerciseId,
      exerciseName: entry.exerciseName,
      date: entry.date,
      maxWeight,
      maxReps,
      totalVolume: entry.totalVolume,
    };
  });
}

/**
 * @param {object[]} workouts
 * @returns {number}
 */
export function workoutsCompleted(workouts) {
  return Array.isArray(workouts) ? workouts.length : 0;
}

/**
 * Workouts per week: average and optional per-week counts.
 * @param {object[]} workouts
 * @returns {{ avg: number, perWeek: { weekStart: Date, count: number }[] }}
 */
export function workoutsPerWeek(workouts) {
  if (!Array.isArray(workouts) || workouts.length === 0) {
    return { avg: 0, perWeek: [] };
  }
  const weekCounts = new Map();
  for (const w of workouts) {
    const d = getWorkoutDate(w);
    const weekStart = startOfWeek(d, { weekStartsOn: 1 });
    const ts = weekStart.getTime();
    weekCounts.set(ts, (weekCounts.get(ts) ?? 0) + 1);
  }
  const perWeek = Array.from(weekCounts.entries())
    .map(([ts, count]) => ({ weekStart: new Date(Number(ts)), count }))
    .sort((a, b) => a.weekStart - b.weekStart);
  const totalWeeks = perWeek.length || 1;
  const totalWorkouts = workouts.length;
  return { avg: Math.round((totalWorkouts / totalWeeks) * 10) / 10, perWeek };
}

/**
 * Total volume from flattened data points.
 * @param {{ volume: number }[]} dataPoints
 * @returns {number}
 */
export function totalVolume(dataPoints) {
  if (!Array.isArray(dataPoints)) return 0;
  return dataPoints.reduce((sum, p) => sum + (p.volume ?? 0), 0);
}

/**
 * Consecutive weeks with at least one workout (from workout dates).
 * @param {object[]} workouts
 * @returns {number}
 */
export function consecutiveWeeksWithWorkouts(workouts) {
  if (!Array.isArray(workouts) || workouts.length === 0) return 0;
  const weekStarts = new Set(
    workouts.map((w) => startOfWeek(getWorkoutDate(w), { weekStartsOn: 1 }).getTime())
  );
  const sorted = Array.from(weekStarts).sort((a, b) => b - a);
  let streak = 0;
  const now = Date.now();
  const thisWeek = startOfWeek(new Date(now), { weekStartsOn: 1 }).getTime();
  if (!sorted.includes(thisWeek)) return 0;
  let cursor = thisWeek;
  for (let i = 0; i < 52; i++) {
    if (weekStarts.has(cursor)) {
      streak++;
      cursor = subWeeks(new Date(cursor), 1).getTime();
    } else {
      break;
    }
  }
  return streak;
}

/**
 * Find exercise with largest improvement in max weight or volume (first vs last occurrence in window).
 * @param {{ exerciseId: string, exerciseName: string, date: Date, maxWeight: number, maxReps: number, totalVolume: number }[]} aggregated
 * @returns {{ exerciseId: string, exerciseName: string, metric: 'weight'|'volume', first: number, last: number, change: number, changePct: number } | null}
 */
export function mostImprovedExercise(aggregated) {
  if (!Array.isArray(aggregated) || aggregated.length === 0) return null;
  const byExercise = new Map();
  for (const row of aggregated) {
    const id = row.exerciseId;
    if (!id) continue;
    if (!byExercise.has(id)) {
      byExercise.set(id, {
        exerciseId: id,
        exerciseName: row.exerciseName,
        rows: [],
      });
    }
    const entry = byExercise.get(id);
    entry.rows.push({
      date: row.date,
      maxWeight: row.maxWeight ?? 0,
      totalVolume: row.totalVolume ?? 0,
    });
    if (row.exerciseName) entry.exerciseName = row.exerciseName;
  }
  let best = null;
  let bestImprovement = -1;
  for (const entry of byExercise.values()) {
    const rows = entry.rows.sort((a, b) => a.date - b.date);
    if (rows.length < 2) continue;
    const first = rows[0];
    const last = rows[rows.length - 1];
    const weightChange = last.maxWeight - first.maxWeight;
    const weightPct =
      first.maxWeight > 0 ? (weightChange / first.maxWeight) * 100 : weightChange;
    const volChange = last.totalVolume - first.totalVolume;
    const volPct =
      first.totalVolume > 0 ? (volChange / first.totalVolume) * 100 : 0;
    if (weightChange > 0 && weightPct > bestImprovement) {
      bestImprovement = weightPct;
      best = {
        exerciseId: entry.exerciseId,
        exerciseName: entry.exerciseName,
        metric: "weight",
        first: first.maxWeight,
        last: last.maxWeight,
        change: weightChange,
        changePct: weightPct,
      };
    }
    if (volChange > 0 && volPct > bestImprovement) {
      bestImprovement = volPct;
      best = {
        exerciseId: entry.exerciseId,
        exerciseName: entry.exerciseName,
        metric: "volume",
        first: first.totalVolume,
        last: last.totalVolume,
        change: volChange,
        changePct: volPct,
      };
    }
  }
  return best;
}

/**
 * Personal records in the period: heaviest single set per exercise (max weight in window).
 * @param {{ exerciseId: string, exerciseName: string, date: Date, maxWeight: number, maxReps: number, totalVolume: number }[]} aggregated
 * @returns {{ exerciseId: string, exerciseName: string, weight: number, reps: number, date: Date }[]}
 */
export function personalRecordsInPeriod(aggregated) {
  if (!Array.isArray(aggregated)) return [];
  const byExercise = new Map();
  for (const row of aggregated) {
    const id = row.exerciseId;
    if (!id) continue;
    const w = row.maxWeight ?? 0;
    const r = row.maxReps ?? 0;
    const existing = byExercise.get(id);
    if (
      !existing ||
      w > existing.weight ||
      (w === existing.weight && r > existing.reps)
    ) {
      byExercise.set(id, {
        exerciseId: id,
        exerciseName: row.exerciseName ?? "Unknown",
        weight: w,
        reps: r,
        date: row.date,
      });
    }
  }
  return Array.from(byExercise.values()).filter((pr) => pr.weight > 0 || pr.reps > 0);
}

/**
 * Weekly volume series for charts: { weekStart, volume }.
 * @param {object[]} workouts
 * @param {{ date: Date, volume: number }[]} dataPoints - from flattening all workouts
 * @returns {{ weekStart: Date, volume: number }[]}
 */
export function weeklyVolumeSeries(workouts, dataPoints) {
  if (!Array.isArray(dataPoints) || dataPoints.length === 0) return [];
  const byWeek = new Map();
  for (const p of dataPoints) {
    const d = p.date instanceof Date ? p.date : new Date(p.date);
    const weekStart = startOfWeek(d, { weekStartsOn: 1 });
    const ts = weekStart.getTime();
    byWeek.set(ts, (byWeek.get(ts) ?? 0) + (p.volume ?? 0));
  }
  return Array.from(byWeek.entries())
    .map(([ts, volume]) => ({ weekStart: new Date(Number(ts)), volume }))
    .sort((a, b) => a.weekStart - b.weekStart);
}

/**
 * Average training volume per calendar week that had logged sets (matches {@link weeklyVolumeSeries} length).
 * Units: effective weight × reps summed per week.
 * @param {number} totalVolumeValue - period total from {@link totalVolume}
 * @param {{ weekStart: Date, volume: number }[]} weeklyVolSeries
 */
export function averageWeeklyVolume(totalVolumeValue, weeklyVolSeries) {
  if (!Array.isArray(weeklyVolSeries) || weeklyVolSeries.length === 0) return 0;
  const total = Number(totalVolumeValue);
  if (!Number.isFinite(total) || total <= 0) return 0;
  return Math.round(total / weeklyVolSeries.length);
}

/**
 * Exercises with at least minSessions occurrences (for "by exercise" list).
 * Returns aggregated rows grouped by exerciseId, with first/last stats.
 * @param {object[]} aggregated - from aggregateByExerciseAndDate
 * @param {number} minSessions
 * @returns {{ exerciseId: string, exerciseName: string, sessions: number, firstMaxWeight: number, lastMaxWeight: number, firstVolume: number, lastVolume: number }[]}
 */
export function exercisesWithProgression(aggregated, minSessions = 2) {
  if (!Array.isArray(aggregated)) return [];
  const byExercise = new Map();
  for (const row of aggregated) {
    const id = row.exerciseId;
    if (!id) continue;
    if (!byExercise.has(id)) {
      byExercise.set(id, {
        exerciseId: id,
        exerciseName: row.exerciseName,
        sessions: 0,
        firstMaxWeight: null,
        lastMaxWeight: null,
        firstVolume: null,
        lastVolume: null,
        rows: [],
      });
    }
    const entry = byExercise.get(id);
    entry.sessions++;
    entry.rows.push(row);
    if (row.exerciseName) entry.exerciseName = row.exerciseName;
  }
  const result = [];
  for (const entry of byExercise.values()) {
    if (entry.sessions < minSessions) continue;
    const rows = entry.rows.sort((a, b) => a.date - b.date);
    const first = rows[0];
    const last = rows[rows.length - 1];
    result.push({
      exerciseId: entry.exerciseId,
      exerciseName: entry.exerciseName,
      sessions: entry.sessions,
      firstMaxWeight: first.maxWeight ?? 0,
      lastMaxWeight: last.maxWeight ?? 0,
      firstVolume: first.totalVolume ?? 0,
      lastVolume: last.totalVolume ?? 0,
    });
  }
  return result.sort((a, b) => b.sessions - a.sessions);
}

/**
 * Get time series for a single exercise from aggregated data (for progress charts).
 * @param {{ exerciseId: string, date: Date, maxWeight: number, totalVolume: number }[]} aggregated - from aggregateByExerciseAndDate
 * @param {string} exerciseId
 * @returns {{ date: Date, maxWeight: number, totalVolume: number }[]}
 */
export function getExerciseProgressSeries(aggregated, exerciseId) {
  if (!Array.isArray(aggregated) || !exerciseId) return [];
  return aggregated
    .filter((r) => r.exerciseId === exerciseId)
    .sort((a, b) => a.date - b.date)
    .map((r) => ({ date: r.date, maxWeight: r.maxWeight ?? 0, totalVolume: r.totalVolume ?? 0 }));
}
